import { Button } from "@mantine/core";
import { useCallback, useState } from "react";
import { useToast } from "../../../../../mantine/notifications/useToast";
import { getFieldSpecificError } from "../../../../../utils/api-utils";
import { getErrorText } from "../../../../../utils/get-error-text";
import {
  type EnergyDataImportRequest,
  retryUploadWithParameterSetRequest
} from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import { ReimportDataImportRequestsModal } from "./ReimportDataImportRequestsModal";

interface ReimportDataImportRequestsButtonProps {
  dataImportRequests: EnergyDataImportRequest[];
  onClearSelection: () => void;
  onReimport: () => void;
}

function ReimportDataImportRequestsButton({
  dataImportRequests,
  onClearSelection,
  onReimport
}: ReimportDataImportRequestsButtonProps) {
  const { showErrorToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dataImportRequestsNames = dataImportRequests.map(
    ({ energyDataFile: { name } }) => name
  );

  const toggleModal = () =>
    setIsModalOpen((currentIsModalOpen) => !currentIsModalOpen);

  const handleClickReimport = useCallback(
    (parameterSetId: string) =>
      Promise.allSettled(
        dataImportRequests.map(({ id }) =>
          retryUploadWithParameterSetRequest(id, parameterSetId)
        )
      )
        .then((results) => {
          const rejectedResult = results.find(
            (result) => result.status === "rejected"
          );

          if (rejectedResult) {
            const { reason } = rejectedResult as PromiseRejectedResult;

            const errorMessage = getFieldSpecificError(reason, [
              "parameterSetId"
            ]);

            const errorText = getErrorText(
              reason,
              errorMessage
                ? {
                    [reason?.response?.status as number]: errorMessage
                  }
                : {}
            );
            showErrorToast(errorText);
          } else {
            onReimport();
          }
        })
        .finally(() => {
          toggleModal();
          onClearSelection();
        }),
    [dataImportRequests, onReimport, onClearSelection, showErrorToast]
  );

  return (
    <>
      <Button disabled={dataImportRequests.length === 0} onClick={toggleModal}>
        Reimport
      </Button>
      {isModalOpen && (
        <ReimportDataImportRequestsModal
          dataImportRequestsNames={dataImportRequestsNames}
          onAction={handleClickReimport}
          onToggle={toggleModal}
        />
      )}
    </>
  );
}

export { ReimportDataImportRequestsButton };
